import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Button, Typography, useTheme, useMediaQuery, Grid } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import {
    getAuthToken,
    MovieInfo,
    Loading,
    EnhancedErrorMessage,
    getPaidContent,
    getPublishedEvent
} from '@virtual-cinema/client-lib';
import AudienceAppBar from '../common/app-bar';
import EventBreadcrumbs from './event-breadcrumbs';
import ContentPlaylist from './content-playlist';
import VideoPlayer from './video-player';

// Configure dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

export function ViewContent() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Get parameters from URL
    const { contentId, eventId } = useParams();
    const playerRef = useRef(null);
    const navigate = useNavigate();
    const accessToken = getAuthToken();

    // State variables
    const [eventData, setEventData] = useState(null);
    const [movieData, setMovieData] = useState(null);
    const [contentData, setContentData] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [isEventLive, setIsEventLive] = useState(false);

    // Fetch event data and find the content
    useEffect(() => {
        const fetchEventData = async () => {
            if (eventId) {
                try {
                    setIsLoading(true);
                    const { data, error } = await getPublishedEvent(eventId);
                    if (error) throw new Error(error);
                    
                    // Set event data
                    setEventData(data.event);
                    
                    // Find the content within the event data
                    const content = data.content?.find(item => item.id === contentId);
                    if (content) {
                        setMovieData(content);
                    } else {
                        throw new Error("Content not found in event");
                    }

                    // Handle event timing
                    if (data.event.valid_from) {
                        const localStartTime = dayjs.utc(data.event.valid_from).local();
                        const currentTime = dayjs();
                        const diffInMs = localStartTime.diff(currentTime);

                        // Check if event is live
                        if (diffInMs <= 0) {
                            setIsEventLive(true);
                            setTimeRemaining(0);
                        } else {
                            setTimeRemaining(diffInMs);
                        }
                    }
                } catch (error) {
                    setError(`Failed to load content: ${error.message}`);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchEventData();
    }, [eventId, contentId]);

    // Fetch content data if we have access token and event ID
    useEffect(() => {
        const fetchContentData = async () => {
            if (accessToken && contentId && eventId && movieData) {
                try {
                    setIsLoading(true);
                    const { data, error } = await getPaidContent(contentId, accessToken);
                    if (error) throw new Error(error);
                    setContentData(data);

                    // Check if event is live based on content data
                    if (data.eventLive) {
                        setIsEventLive(true);
                        setTimeRemaining(0);
                    }
                } catch (error) {
                    setError(`Failed to fetch content. Please try again.`);
                } finally {
                    setIsLoading(false);
                }
            } else if (movieData) {
                setIsLoading(false);
            }
        };

        fetchContentData();
    }, [contentId, eventId, accessToken, movieData]);

    // Effect for upcoming event countdown - only runs if event is not live
    useEffect(() => {
        let interval;
        if (timeRemaining > 0 && !isEventLive) {
            interval = setInterval(() => {
                setTimeRemaining(prevTime => {
                    const newTime = Math.max(0, prevTime - 1000);
                    if (newTime === 0) {
                        setIsEventLive(true);
                    }
                    return newTime;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [timeRemaining, isEventLive]);

    const handleLogin = () => {
        navigate("/login");
    };

    const handleBuyTickets = () => {
        if (eventId) {
            navigate(`/event/${eventId}#tickets`, {
                state: {
                    scrollToTickets: true,
                    behavior: 'smooth'
                }
            });
        } else {
            navigate('/');
        }
    };

    const formatTimeDisplay = (ms) => {
        if (!ms && ms !== 0) return "Loading...";

        const duration = dayjs.duration(ms);
        const days = Math.floor(duration.asDays());
        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        return `Available in ${days}d ${hours}h ${minutes}m ${seconds}s`;
    };

    const videoJsOptions = contentData?.contentUrl ? {
        autoplay: true,
        controls: true,
        responsive: true,
        fluid: true,
        sources: [{
            src: contentData.contentUrl,
            type: 'application/x-mpegURL',
        }],
        html5: {
            hls: {
                overrideNative: true,
                withCredentials: true
            },
            nativeVideoTracks: true,
            nativeAudioTracks: true,
            nativeTextTracks: true
        }
    } : null;

    const handlePlayerReady = (player) => {
        playerRef.current = player;

        player.on('waiting', () => {
            console.log('player is waiting');
        });

        player.on('dispose', () => {
            console.log('player will dispose');
        });
    };

    const posterUrl = movieData?.latest_asset?.asset_url;
    const movieTitle = movieData?.info?.title || movieData?.title || "Content";
    const eventTitle = eventData?.title || "Event";

    const CountdownDisplay = () => {
        if (!eventData?.valid_from || isEventLive) {
            return null;
        }

        // Only show countdown if event is not live and there's time remaining
        if (timeRemaining > 0) {
            return (
                <Typography
                    variant={isMobile ? "body1" : "h6"}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        padding: isMobile ? 1 : 2,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        textAlign: 'center',
                        fontWeight: 'medium',
                        letterSpacing: 0.5,
                        fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.25rem'
                        }
                    }}
                >
                    {formatTimeDisplay(timeRemaining)}
                </Typography>
            );
        }

        return null;
    };

    const ContentContainer = () => {
        const hasNoPoster = !posterUrl;

        return (
            <Box
                sx={{
                    position: 'relative',
                    mb: 4,
                    height: '400px',
                    backgroundColor: hasNoPoster ? 'rgba(0, 0, 0, 0.8)' : 'transparent',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    ...(posterUrl && {
                        backgroundImage: `url(${posterUrl})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                    })
                }}
            >
                <CountdownDisplay />
                {!posterUrl && (
                    <Typography
                        variant="h3"
                        sx={{
                            color: 'white',
                            textAlign: 'center',
                            mb: 4
                        }}
                    >
                        {movieData?.title || 'Coming Soon'}
                    </Typography>
                )}
                <ActionButtons />
            </Box>
        );
    };

    const ActionButtons = () => (
        <Box
            sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? 1 : 2,
                alignItems: 'center',
                justifyContent: 'flex-end',
                padding: {
                    xs: 2,
                    sm: 3,
                    md: 4
                },
                background: posterUrl
                    ? 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0.1) 100%)'
                    : 'transparent'
            }}
        >
            {!accessToken ? (
                <Button
                    variant="contained"
                    color="primary"
                    size={isMobile ? "medium" : "large"}
                    onClick={handleLogin}
                    sx={{
                        minWidth: {
                            xs: 150,
                            sm: 180,
                            md: 200
                        },
                        backgroundColor: 'primary.main',
                        '&:hover': {
                            backgroundColor: 'primary.dark'
                        }
                    }}
                >
                    Log In to Watch
                </Button>
            ) : !contentData?.hasPaid && (
                <Button
                    variant="contained"
                    color="primary"
                    size={isMobile ? "medium" : "large"}
                    onClick={handleBuyTickets}
                    sx={{
                        minWidth: {
                            xs: 150,
                            sm: 180,
                            md: 200
                        },
                        backgroundColor: 'primary.main',
                        '&:hover': {
                            backgroundColor: 'primary.dark'
                        }
                    }}
                >
                    Buy Tickets
                </Button>
            )}
        </Box>
    );

    // Loading indicator
    if (isLoading) {
        return (
            <Container maxWidth="xl">
                <AudienceAppBar />
                <Loading />
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <AudienceAppBar />
            {error ? (
                <EnhancedErrorMessage message={error} />
            ) : (
                <>
                    <EventBreadcrumbs
                        eventTitle={eventTitle}
                        contentTitle={movieTitle}
                        eventId={eventId}
                    />

                    <Grid container spacing={3}>
                        {/* Main Content */}
                        <Grid item xs={12} md={8}>
                            {contentData?.contentUrl ? (
                                <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
                                    <VideoPlayer
                                        options={videoJsOptions}
                                        onReady={handlePlayerReady}
                                        contentId={contentId}
                                        eventId={eventId}
                                    />
                                </Box>
                            ) : (
                                <ContentContainer />
                            )}
                            {movieData && <MovieInfo movieData={movieData} />}
                        </Grid>

                        {/* Playlist Sidebar */}
                        <Grid item xs={12} md={4}>
                            <ContentPlaylist
                                eventId={eventId}
                                currentContentId={contentId}
                            />
                        </Grid>
                    </Grid>
                </>
            )}
        </Container>
    );
}

export default ViewContent;